import React, { useState } from "react";
import styled from "styled-components";
import { FiDownload, FiTrash2, FiCopy, FiRefreshCw } from "react-icons/fi";
import { theme } from "../../styles/theme";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/StatusBadge";
import { updateVideoNotes } from "../../services/api";
import { Modal } from "../ui/Modal";

const VIDEO_RENDER_MODEL_LABELS = {
  default: "grok imagine video",
  fast: "veo 3.1 fast",
  pro: "veo 3.1",
};

const VIDEO_RENDER_MODEL_NAME_OVERRIDES = {
  kling_v26_motion_control: "kling motion control",
};

function getVideoModelLabel(video) {
  if (video?.render_model) {
    return VIDEO_RENDER_MODEL_NAME_OVERRIDES[video.render_model] || video.render_model;
  }

  if (video?.render_quality) {
    return VIDEO_RENDER_MODEL_LABELS[video.render_quality] || video.render_quality;
  }

  return null;
}

function getSeedImageUrl(video) {
  return video?.source_image_url || video?.preview_image_url || null;
}

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(180px, 1.2fr);
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border: ${theme.borders.base};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.secondary};
  flex: 1 1 calc(50% - ${theme.spacing.sm});
  min-width: 340px;
`;

const Preview = styled.div`
  width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #000;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  min-height: 100%;
`;

const VideoBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
  align-items: flex-start;
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  flex: 1;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: ${theme.borders.base};
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  font-family: inherit;
  font-size: ${theme.typography.fontSize.sm};
  resize: vertical;
`;

const VideoActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing["2xs"]};
  width: 100%;
  align-items: stretch;
  margin-top: auto;
`;

function VideosPanel({ videos, onDelete, onDupe, onCopy, onRetry }) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptVideo, setPromptVideo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [retryingVideoId, setRetryingVideoId] = useState(null);

  if (!videos.length) {
    return <span style={{ color: theme.colors.text.muted }}>No rendered videos yet.</span>;
  }

  return (
    <Grid>
      {videos.map((video) => (
        <Card key={video.id}>
          <Preview>
            {video.result_video_url || video.raw_video_url ? (
              <PreviewVideo
                src={video.result_video_url || video.raw_video_url}
                poster={video.preview_image_url || video.source_image_url || undefined}
                controls
                preload="metadata"
                playsInline
                muted
              />
            ) : video.preview_image_url ? (
              <PreviewVideo
                src={video.preview_image_url}
                poster={video.preview_image_url || video.source_image_url || undefined}
                controls
                preload="metadata"
                playsInline
                muted
              />
            ) : video.source_image_url ? (
              <PreviewVideo
                src={video.source_image_url}
                poster={video.source_image_url}
                controls
                preload="metadata"
                playsInline
                muted
              />
            ) : null}
          </Preview>

          <RightColumn>
            <VideoBody>
              <div style={{ display: "flex", gap: theme.spacing["2xs"], alignItems: "center" }}>
                <StatusBadge status={video.status}>{video.status}</StatusBadge>
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    setPromptVideo(video);
                    setPromptOpen(true);
                    setCopied(false);
                  }}
                  style={{ padding: `${theme.spacing["3xs"]} ${theme.spacing.xs}` }}
                >
                  View prompt
                </Button>
              </div>
              {getVideoModelLabel(video) && (
                <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                  Model: {getVideoModelLabel(video)}
                </span>
              )}
            </VideoBody>

            <NotesArea
              defaultValue={video.notes || ""}
              rows={5}
              onBlur={(e) => {
                const val = e.target.value;
                updateVideoNotes(video.id, val).catch((err) =>
                  console.error("Failed to update notes", err)
                );
              }}
              placeholder="Notes for this video..."
            />

            <VideoActions>
              {video.result_video_url && (
                <Button
                  as="a"
                  size="small"
                  variant="secondary"
                  href={video.result_video_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiDownload /> Download processed
                </Button>
              )}
              {getSeedImageUrl(video) && (
                <Button
                  as="a"
                  size="small"
                  variant="secondary"
                  href={getSeedImageUrl(video)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiDownload /> Download Seed
                </Button>
              )}
              {video.result_video_url && (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => onDupe?.(video)}
                  disabled={video.status !== "completed"}
                >
                  Dupe
                </Button>
              )}
              {video.result_video_url && (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => onCopy?.(video)}
                  disabled={video.status !== "completed"}
                >
                  Copy
                </Button>
              )}
              {video.raw_video_url && (
                <Button
                  as="a"
                  size="small"
                  variant="ghost"
                  href={video.raw_video_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiDownload /> Download raw
                </Button>
              )}
              {video.status === "failed" && (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={async () => {
                    if (!onRetry || retryingVideoId === video.id) return;
                    setRetryingVideoId(video.id);
                    try {
                      await onRetry(video);
                    } finally {
                      setRetryingVideoId(null);
                    }
                  }}
                  disabled={!onRetry || retryingVideoId === video.id}
                >
                  <FiRefreshCw /> {retryingVideoId === video.id ? "Retrying..." : "Retry"}
                </Button>
              )}
              <Button size="small" variant="ghost" onClick={() => onDelete(video)}>
                <FiTrash2 /> Delete
              </Button>
            </VideoActions>
          </RightColumn>
        </Card>
      ))}

      <Modal isOpen={promptOpen} onClose={() => setPromptOpen(false)} title="Video prompt">
        <div style={{ position: "relative" }}>
          <div style={{ whiteSpace: "pre-wrap", color: theme.colors.text.primary, marginBottom: theme.spacing.md }}>
            {promptVideo?.video_prompt || "(No prompt recorded)"}
          </div>
          {promptVideo?.video_prompt && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(promptVideo.video_prompt);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <FiCopy /> {copied ? "Copied!" : "Copy prompt"}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </Grid>
  );
}

export default VideosPanel;
